<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
    <!-- 页面头部 -->
    <div class="flex items-center justify-between mb-6">
      <div class="flex items-center gap-4">
        <button @click="handleBack"
          class="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors">
          <ArrowLeftIcon class="w-6 h-6" />
        </button>
        <h1 class="text-3xl font-bold text-gray-900 dark:text-gray-100">公告详情</h1>
      </div>
      <div class="flex gap-3">
        <button @click="handleEdit"
          class="primary-btn flex items-center gap-2">
          <PencilIcon class="w-5 h-5" />
          编辑
        </button>
        <button @click="handleDelete"
          class="danger-btn flex items-center gap-2">
          <TrashIcon class="w-5 h-5" />
          删除
        </button>
      </div>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="flex justify-center items-center py-20">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
    </div>

    <!-- 公告内容 -->
    <div v-else-if="message" class="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
      <!-- 标题和优先级 -->
      <div class="flex items-center gap-3 mb-4">
        <span :class="getPriorityBadgeClass(message.priority)" class="px-3 py-1 text-sm font-medium rounded">
          {{ MessagePriorityLabel[message.priority] }}
        </span>
        <h2 class="text-2xl font-bold text-gray-900 dark:text-gray-100">{{ message.title }}</h2>
      </div>

      <!-- 元信息 -->
      <div
        class="flex items-center gap-6 text-sm text-gray-600 dark:text-gray-400 mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
        <span>发送时间: {{ formatDate(message.createdAt) }}</span>
        <span>消息类型: {{ message.type === 'SYSTEM_NOTIFICATION' ? '系统通知' : '私信' }}</span>
      </div>

      <!-- 消息内容 -->
      <div class="prose dark:prose-invert max-w-none mb-6" v-html="message.content"></div>

      <!-- 统计信息 -->
      <div>
        <!-- 统计信息 -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <div class="text-center">
            <div class="text-2xl font-bold text-gray-900 dark:text-gray-100">{{ message.stats.totalRecipients }}</div>
            <div class="text-sm text-gray-600 dark:text-gray-400">接收人数</div>
          </div>
          <div class="text-center">
            <div class="text-2xl font-bold text-green-600 dark:text-green-400">{{ message.stats.readCount }}</div>
            <div class="text-sm text-gray-600 dark:text-gray-400">已读人数</div>
          </div>
          <div class="text-center">
            <div class="text-2xl font-bold text-gray-600 dark:text-gray-400">{{ message.stats.unreadCount }}</div>
            <div class="text-sm text-gray-600 dark:text-gray-400">未读人数</div>
          </div>
        </div>

        <!-- 接收范围 -->
        <div class="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">接收范围</h3>
          <p class="text-sm text-gray-600 dark:text-gray-400">
            {{ message.stats.totalRecipients > 0 ? `已发送给 ${message.stats.totalRecipients} 位用户` : '发送给所有用户' }}
          </p>
        </div>
      </div>
    </div>

    <!-- 错误状态 -->
    <div v-else class="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-12 text-center">
      <p class="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">公告不存在</p>
      <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">该公告可能已被删除</p>
      <button @click="handleBack"
        class="primary-btn px-6">
        返回列表
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { ArrowLeftIcon, PencilIcon, TrashIcon } from '@heroicons/vue/24/outline'
import { getMessageAPI, deleteMessageAPI } from '@/api/message'
import toaster from '@/utils/toaster'
import { formatDate } from '@/utils/date'
import { MessagePriorityLabel, MessagePriority } from '@/types/message'
import type { MessageDetail } from '@/types/message'

/**
 * 路由实例
 */
const router = useRouter()
const route = useRoute()

/**
 * 公告数据
 */
const message = ref<MessageDetail | null>(null)
const loading = ref(true)

/**
 * 获取优先级徽章样式
 */
const getPriorityBadgeClass = (priority: MessagePriority): string => {
  const classMap = {
    [MessagePriority.HIGH]: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    [MessagePriority.NORMAL]: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    [MessagePriority.LOW]: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  }
  return classMap[priority] || classMap[MessagePriority.NORMAL]
}

/**
 * 加载公告详情
 */
const loadMessage = async () => {
  const messageId = parseInt(route.params.id as string)
  if (isNaN(messageId)) {
    toaster.error('无效的公告ID')
    router.push('/message')
    return
  }

  try {
    loading.value = true
    const response = await getMessageAPI(messageId)

    if (response.status === 200 || response.status === 'success') {
      // API返回的数据结构: { message: {...}, stats: {...} }
      // 直接使用,前后端字段名已统一
      message.value = {
        ...response.data.message,
        stats: response.data.stats,
      }
    } else {
      toaster.error(response.message || '获取公告详情失败')
      message.value = null
    }
  } catch (error: any) {
    console.error('获取公告详情失败:', error)
    toaster.error(error.message || '获取公告详情失败')
    message.value = null
  } finally {
    loading.value = false
  }
}

/**
 * 返回列表页
 */
const handleBack = () => {
  router.push('/message')
}

/**
 * 编辑公告
 */
const handleEdit = () => {
  if (message.value) {
    router.push(`/message/${message.value.id}/edit`)
  }
}

/**
 * 删除公告
 */
const handleDelete = async () => {
  if (!message.value) return

  if (!confirm(`确定要删除公告"${message.value.title}"吗?`)) {
    return
  }

  try {
    const response = await deleteMessageAPI(message.value.id)

    if (response.status === 200 || response.status === 'success') {
      toaster.success('公告删除成功')
      router.push('/message')
    } else {
      toaster.error(response.message || '公告删除失败')
    }
  } catch (error: any) {
    console.error('删除公告失败:', error)
    toaster.error(error.message || '公告删除失败')
  }
}

/**
 * 组件挂载时加载数据
 */
onMounted(() => {
  loadMessage()
})
</script>
